import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Customer, Part } from '@visionqc/shared';
import { useIsAdmin } from '../api/auth';
import { api } from '../api/client';
import { procguardApi } from '../api/procguard';

/**
 * The per-customer parts master — the source of truth for what a kit contains.
 * Component part numbers in a procedure resolve against this list.
 */
export function ProcGuardPartsView() {
  const isAdmin = useIsAdmin();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [parts, setParts] = useState<Part[]>([]);
  const [pn, setPn] = useState('');
  const [desc, setDesc] = useState('');
  const [paste, setPaste] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.listCustomers().then((cs) => { setCustomers(cs); setCustomerId((id) => id ?? cs[0]?.id ?? null); }).catch(() => undefined);
  }, []);
  const load = useCallback(() => {
    if (!customerId) return;
    setLoading(true);
    procguardApi.listParts(customerId).then(setParts).catch((e) => setErr(String(e.message))).finally(() => setLoading(false));
  }, [customerId]);
  useEffect(() => load(), [load]);

  if (!isAdmin) return <div className="panel muted">The parts master requires the ADMIN role.</div>;

  async function add() {
    if (!customerId || pn.trim().length < 1) return;
    try { await procguardApi.createPart({ customerId, partNumber: pn.trim(), description: desc }); setPn(''); setDesc(''); load(); }
    catch (e) { setErr((e as Error).message); }
  }
  async function del(id: number) {
    try { await procguardApi.deletePart(id); load(); } catch (e) { setErr((e as Error).message); }
  }
  async function runImport() {
    if (!customerId) return;
    // Accept "partNumber, description, unit" lines (unit optional).
    const rows = paste.split('\n').map((l) => l.trim()).filter(Boolean).map((l) => {
      const [partNumber, description = '', unit = 'ea'] = l.split(/\s*[,;\t]\s*/);
      return { partNumber: partNumber!, description, unit };
    }).filter((r) => r.partNumber);
    if (rows.length === 0) return;
    try { await procguardApi.importParts(customerId, rows); setPaste(''); setShowImport(false); load(); }
    catch (e) { setErr((e as Error).message); }
  }

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <h1 className="view-title">Jakatori — parts master</h1>
        <Link className="btn sm" to="/procguard">← Workstations</Link>
      </div>
      {err && <div className="panel" style={{ color: 'var(--bad)' }}>{err}</div>}

      <div className="panel" style={{ marginBottom: 16 }}>
        <label>Customer</label>
        <select value={customerId ?? ''} onChange={(e) => setCustomerId(Number(e.target.value))}>
          {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div><label>Part number</label><input value={pn} onChange={(e) => setPn(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && pn.trim() && void add()} placeholder="M6-20" /></div>
          <div style={{ flex: 1, minWidth: 180 }}><label>Description</label><input value={desc} onChange={(e) => setDesc(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && pn.trim() && void add()} placeholder="Hex bolt M6×20" style={{ width: '100%' }} /></div>
          <button className="btn sm primary" onClick={() => void add()} disabled={pn.trim().length < 1}>Add part</button>
          <button className="btn sm" onClick={() => setShowImport((s) => !s)}>{showImport ? 'Cancel import' : 'Bulk import'}</button>
        </div>
        {showImport && (
          <div style={{ marginTop: 12 }}>
            <label>Paste lines: <span className="muted">partNumber, description, unit</span></label>
            <textarea value={paste} onChange={(e) => setPaste(e.target.value)} rows={5} style={{ width: '100%' }} placeholder={'M6-20, Hex bolt M6x20, ea\nNUT-M6, Hex nut M6, ea'} />
            <button className="btn sm primary" style={{ marginTop: 8 }} onClick={() => void runImport()}>Import</button>
          </div>
        )}
      </div>

      {parts.length === 0 ? (
        <div className="panel muted">{loading ? 'Loading…' : 'No parts yet. Add them above, or they appear automatically the first time an SOP step uses one.'}</div>
      ) : (
        <div className="panel">
          <table style={{ width: '100%' }}>
            <thead><tr><th>Part number</th><th>Description</th><th>Unit</th><th></th></tr></thead>
            <tbody>
              {parts.map((p) => (
                <tr key={p.id}>
                  <td style={{ fontFamily: 'var(--mono)' }}>{p.partNumber}</td>
                  <td>{p.description || <span className="muted">—</span>}</td>
                  <td className="muted">{p.unit}</td>
                  <td style={{ textAlign: 'right' }}><button className="btn sm bad" onClick={() => void del(p.id)}>Delete</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
